<template>
  <div class="card mt-5 mb-8">
    <div class="card-header">
      <h3 class="card-title">Christmas Order</h3>
    </div>
    <div class="card-body">
      <form ref="form" novalidate @submit.prevent="submitForm">
        <div class="row">
          <div class="col-lg-6">
            <div class="mb-3">
              <label class="form-label" for="co-firstname">Firstname</label>
              <input :class="validation.firstname" type="text" class="form-control" v-model="firstname" id="co-firstname" name="firstname" autocomplete="given-name">
              <div v-if="errors.firstname" class="field-error" role="alert">{{ errors.firstname }}</div>
            </div>

          </div>
          <div class="col-lg-6">
            <div class="mb-3">
              <label class="form-label" for="co-lastname">Lastname</label>
              <input :class="validation.lastname" type="text" class="form-control" v-model="lastname" id="co-lastname" name="lastname" autocomplete="family-name">
              <div v-if="errors.lastname" class="field-error" role="alert">{{ errors.lastname }}</div>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="mb-3">
              <label class="form-label" for="co-email">Email</label>
              <input :class="validation.email" type="email" class="form-control" v-model="email" id="co-email" name="email" autocomplete="email">
              <div v-if="errors.email" class="field-error" role="alert">{{ errors.email }}</div>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="mb-3">
              <label class="form-label" for="co-phone">Phone No.</label>
              <input :class="validation.phone" type="tel" class="form-control" v-model="phone" id="co-phone" name="phone" autocomplete="tel">
              <div v-if="errors.phone" class="field-error" role="alert">{{ errors.phone }}</div>
            </div>
          </div>
          <div class="row mb-3">
            <hr class="my-5">
            <div class="col"></div>
            <div class="btn-group mt-3 col-md-8">
              <button
                  type="button"
                  class="btn btn-outline-primary"
                  :class="{ active: selectedOption === 'Collection' }"
                  @click="selectedOption = 'Collection'">Collection
              </button>

              <button
                  v-if="deliveryDates.length > 0"
                      type="button"
                      class="btn btn-outline-primary"
                      :class="{ active: selectedOption === 'Delivery', disabled: deliveryDates.length === 0 }"
                      @click="selectedOption = 'Delivery'">Delivery (£10.00)
              </button>
              <button
                  v-else
                  type="button"
                  class="btn btn-outline-primary"
                  :class="{ active: selectedOption === 'Delivery', disabled: deliveryDates.length === 0 }"
                  @click="selectedOption = 'Delivery'">Delivery (fully booked)
              </button>
            </div>
            <div class="col"></div>
          </div>
          <div v-if="selectedOption === 'Collection'">
            Collection:
            <div class="alert alert-info" v-if="selectedDate && getSelectedDateMessage()">
              {{ getSelectedDateMessage() }}
            </div>
            <div class="mb-3 mt-3 row">
              <div class="col-md-4">
                <label class="form-label" for="co-location">Collection Location</label>
                <div v-if="errors.selectedLocation" class="field-error" role="alert">{{ errors.selectedLocation }}</div>
                <select
                    id="co-location"
                    :class="validation.selectedLocation"
                    class="form-select"
                    v-model="selectedLocation"
                >
                  <option
                      :disabled="!location.enabled"
                      v-for="location in collectionLocations"
                      :key="location.collectionValue"
                      :value="location.collectionLocation"
                  >
                    {{ location.collectionLocation }} - {{ formatCurrency(location.collectionFee) }}
                    <span v-if="!location.enabled">(Not Available)</span>
                  </option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label" for="co-date">Collection Date</label>
                <div v-if="errors.selectedDate" class="field-error" role="alert">{{ errors.selectedDate }}</div>
                <select
                    id="co-date"
                    :class="validation.selectedDate"
                    class="form-select"
                    v-model="selectedDate"
                    :disabled="!selectedLocation"
                >
                  <option
                      :disabled="!option.enabled"
                      v-for="option in filteredDates"
                      :key="option.collectionOptionId"
                      :value="option.collectionDate"
                  >
                    {{ option.collectionDate }}
                    <span v-if="!option.enabled">(Not Available)</span>
                  </option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label" for="co-time">Collection Time</label>
                <div v-if="errors.selectedTime" class="field-error" role="alert">{{ errors.selectedTime }}</div>
                <select
                    id="co-time"
                    :class="validation.selectedTime"
                    class="form-select"
                    v-model="selectedTime"
                    :disabled="!selectedDate"
                >
                  <option
                      v-for="option in filteredTimes"
                      :key="option.collectionSlotId"
                      :value="option.collectionTime"
                  >
                    {{ option.collectionTime }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div v-if="selectedOption === 'Delivery'">
            <!-- Delivery Date Dropdown -->
            Delivery:
            <div class="row mt-3 mb-3">
              <div class="col-md-4">
                <label class="form-label" for="co-delivery-date">Delivery Date</label>
                <div v-if="errors.selectedDeliveryDate" class="field-error" role="alert">{{ errors.selectedDeliveryDate }}</div>
                <select id="co-delivery-date" :class="validation.selectedDeliveryDate" class="form-select"
                        v-model="selectedDeliveryDate">
                  <option v-for="option in deliveryDates" :key="option.deliveryValue"
                          :value="option.deliveryDate">
                    {{ option.deliveryDate }}
                  </option>
                </select>
              </div>

              <div class="col-md-4">
                <!-- UK Postcode Input -->
                <label class="form-label" for="co-postcode">Postcode</label>
                <div class="input-group">
                  <input :class="validation.postcode" type="text" class="form-control" v-uk-postcode
                         v-model="postcode" id="co-postcode" name="postcode" autocomplete="postal-code"
                         @keydown.enter.prevent="searchPostcode">
                  <button class="btn btn-outline-primary" type="button" :disabled="isSearching || !postcode"
                          @click="searchPostcode">{{ isSearching ? 'Searching…' : 'Find address' }}
                  </button>
                </div>
                <div v-if="errors.postcode" class="field-error" role="alert">{{ errors.postcode }}</div>
              </div>
              <!-- Address Dropdown -->
              <div class="col-md-4">
                <div class="mb-3">
                  <label class="form-label" for="co-address-select">Select Address</label>
                  <select id="co-address-select" :class="validation.selectedAddress" class="form-select"
                          v-model="selectedAddress" :disabled="!addresses?.length">
                    <option v-for="address in addresses" :key="address.id" :value="address.address">
                      {{ address.address }}
                    </option>
                  </select>
                  <button type="button" class="btn btn-link p-0 mt-1" v-if="postcode && !showManualAddressInput"
                          @click="showManualInput">
                    {{ addresses?.length ? "My address isn't listed" : 'Enter my address by hand' }}
                  </button>
                </div>
              </div>
            </div>
            <div v-if="lookupMessage" class="alert alert-info" role="status">{{ lookupMessage }}</div>
            <div v-if="showManualAddressInput" class="mb-3">
              <label class="form-label" for="co-manual-address">Delivery address</label>
              <textarea :class="validation.manualAddress" id="co-manual-address" name="address"
                        autocomplete="street-address" v-model="manualAddress" class="form-control"
                        rows="3" placeholder="House number and street, town"></textarea>
            </div>
            <div v-if="errors.address" class="field-error mb-3" role="alert">{{ errors.address }}</div>
          </div>

        </div>
        <hr class="my-5">
        <div>
          <label class="form-label" for="co-comments">Comments or allergy requirements</label>
          <textarea v-model="comments" id="co-comments" name="comments" class="form-control mt-2" rows="3"
                    placeholder="Please let us know any comments or allergy requirements you might have"></textarea>
        </div>

        <hr class="my-5">
        <div class="row">
          <h3>Jamie's Farm Charity Donation</h3>
          <div class="row">
            <div class="col-md-3">
              <img src="https://static.larkhallbutchers.co.uk/jamies_farm.svg" alt="Jamie's Farm">

            </div>
            <div class="col">
              We have been working proudly and exclusively with Jamie’s Farm for several years, getting
              our pork and lamb from them.
              Jamie’s Farm is a fantastic charity with a programme that focuses on improving behavior,
              engagement, wellbeing, and essential life skills for young people. They offer a unique,
              preventative solution which empowers youths to choose the best paths for their futures.
              This year, so that they can continue the fantastic work they do with young people, we have
              decided to raise some money for Jamie's Farm and we hope that you can help.
              <br/>
              Jamie’s Farm Registered Charity: 1129544<br/>
              <a href="https://jamiesfarm.org.uk/about/" target="_blank">jamiesfarm.org.uk/about</a>
              <br/>
              <div class="mt-3  btn-group" style="flex-wrap: wrap;">
                <button type="button" v-for="option in donationOptions" :key="option.value"
                        :class="['btn', selectedDonation === option.value ? 'btn-primary' : 'btn-outline-primary']"
                        :aria-pressed="selectedDonation === option.value ? 'true' : 'false'"
                        @click="setDonation(option.value)">
                  {{ option.label }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <hr class="my-5">
        <div class="card bg-light payment-summary" aria-live="polite">
          <div class="card-body">
            <h3 class="mb-3">What you pay</h3>
            <div v-if="basketFailed" class="text-danger mb-2">We couldn't load your basket to work out the amounts. Please reload the page.</div>
            <dl class="row mb-0" v-else>
              <dt class="col-8 fw-normal">Estimated order total</dt>
              <dd class="col-4 text-end" data-testid="est-total">{{ formatCurrency(estimatedTotal) }}</dd>
              <dt class="col-12 fw-normal text-secondary small mb-2">Weights are approximate, so the final total may be a little more or less.</dt>
              <template v-if="depositMode === 'perItem'">
                <dt class="col-8 fw-normal">Order deposit</dt>
                <dd class="col-4 text-end">{{ formatCurrency(depositPolicy.baseDeposit) }}</dd>
                <template v-for="line in depositLines" :key="line.key">
                  <dt class="col-8 fw-normal">Deposit for {{ line.name }}</dt>
                  <dd class="col-4 text-end">{{ formatCurrency(line.amount) }}</dd>
                </template>
              </template>
              <template v-else-if="depositMode === 'fixed'">
                <dt class="col-8 fw-normal">Order deposit</dt>
                <dd class="col-4 text-end" data-testid="order-deposit">{{ formatCurrency(depositTotal) }}</dd>
                <dt class="col-12 fw-normal text-secondary small mb-2" data-testid="order-deposit-note">
                  <template v-if="depositCapped">Your order's estimated total is less than our {{ formatDepositAmount(depositPolicy.orderDeposit) }} deposit, so your deposit is your estimated total.</template>
                  <template v-else>A {{ formatDepositAmount(depositTotal) }} deposit is taken for your whole order.</template>
                </dt>
              </template>
              <template v-else-if="depositMode === 'percent'">
                <dt class="col-8 fw-normal">Order deposit ({{ depositPolicy.orderDepositPercent }}%)</dt>
                <dd class="col-4 text-end" data-testid="order-deposit">{{ formatCurrency(depositTotal) }}</dd>
                <dt class="col-12 fw-normal text-secondary small mb-2" data-testid="order-deposit-note">
                  A deposit of {{ depositPolicy.orderDepositPercent }}% of your estimated order total of {{ formatCurrency(estimatedTotal) }}, rounded down to the nearest pound, is taken today for your whole order.
                </dt>
              </template>
              <template v-else>
                <dt class="col-8 fw-normal">Order deposit</dt>
                <dd class="col-4 text-end text-secondary" data-testid="order-deposit">
                  <span v-if="depositPolicy.status === 'loading'">Working out…</span>
                  <span v-else>Not available</span>
                </dd>
                <dt v-if="depositPolicy.status === 'failed'" class="col-12 fw-normal text-danger small mb-2" role="alert">
                  We couldn't load the deposit amount.
                  <button type="button" class="btn btn-link p-0 align-baseline small" @click="retryDepositPolicy()">Try again</button>
                </dt>
              </template>
              <template v-if="selectedOption === 'Delivery'">
                <dt class="col-8 fw-normal">Home delivery</dt>
                <dd class="col-4 text-end">{{ formatCurrency(1000) }}</dd>
              </template>
              <template v-if="collectionFee > 0">
                <dt class="col-8 fw-normal">{{ selectedLocation }} collection fee</dt>
                <dd class="col-4 text-end">{{ formatCurrency(collectionFee) }}</dd>
              </template>
              <dt v-if="collectionFeePending" class="col-12 fw-normal text-secondary small mb-2">Any collection fee for {{ selectedLocation }} is added once you choose a date.</dt>
              <template v-if="selectedDonation > 0">
                <dt class="col-8 fw-normal">Jamie's Farm donation</dt>
                <dd class="col-4 text-end">{{ formatCurrency(selectedDonation * 100) }}</dd>
              </template>
              <dt class="col-8 h3 mt-2 mb-1">Pay today</dt>
              <dd class="col-4 h3 mt-2 mb-1 text-end" data-testid="pay-today">{{ depositMode ? formatCurrency(payToday) : '–' }}</dd>
              <dt class="col-8 fw-normal">Estimated balance to pay later</dt>
              <dd class="col-4 text-end" data-testid="pay-later">{{ depositMode ? formatCurrency(balanceLater) : '–' }}</dd>
              <dt class="col-12 fw-normal text-secondary small">{{ depositMode === 'fixed' || depositMode === 'percent' ? 'The deposit comes' : 'The deposits come' }} off your final bill. The balance is worked out from the actual weights of your order.</dt>
            </dl>
          </div>
        </div>
        <div v-if="submitError" ref="submitError" class="alert alert-danger mt-3" role="alert" tabindex="-1">{{ submitError }}</div>
        <div class="mb-3 mt-4 text-end">
          <div class="d-inline-block text-start me-3">
            <label class="form-check">
              <input class="form-check-input" type="checkbox" v-model="mailingList">
              <span class="form-check-label">Join our mailing list for promotions and updates</span>
            </label>
          </div>
          <br/>
          <button :disabled="isCheckingOut" type="submit" class="btn btn-primary">{{ isCheckingOut ? 'Please wait…' : 'Go to payment' }}</button>
        </div>
      </form>
    </div>
    <div v-if="showStaff" class="card-footer">
      <label class="form-label" for="co-staff">Staff ID</label>
      <input v-model="staffId" id="co-staff" type="text" class="form-control" autocomplete="off">
    </div>
  </div>
</template>
<script>
import api from "@/services/api";
import { depositPolicy, fixedDepositFor, formatDepositAmount, loadDepositPolicy, percentDepositFor } from "@/services/depositPolicy";

export default {
  name: "CheckoutComponent",
  data() {
    return {
      depositPolicy,
      showStaff: this.$route.query.staff !== undefined,
      basket: [],
      basketFailed: false,
      errors: {},
      submitError: '',
      lookupMessage: '',
      isSearching: false,
      staffId: null,
      firstname: "",
      lastname: "",
      email: "",
      phone: "",
      selectedLocation: null,
      selectedDate: null,
      selectedTime: null,
      collectionLocations: [],
      collectionOptions: [],
      collectionSlots: [],
      selectedOption: 'Collection',
      selectedDeliveryDate: null,
      deliveryDates: [],
      postcode: "",
      addresses: [],
      selectedAddress: null,
      hasSearchedPostcode: false,
      showManualAddressInput: false,
      manualAddress: '',
      mailingList: false,
      comments: '',
      donationOptions: [
        {label: 'No Donation', value: 0},
        {label: '£1 Donation', value: 1},
        {label: '£2 Donation', value: 2},
        {label: '£5 Donation', value: 5},
        {label: '£10 Donation', value: 10},
        {label: '£20 Donation', value: 20}
      ],
      errorMessage: "",
      selectedDonation: 0,
      isCheckingOut: false,
      validation: {
        firstname: null,
        lastname: null,
        email: null,
        phone: null,
        selectedLocation: null,
        selectedDate: null,
        selectedTime: null,
        selectedDeliveryDate: null,
        postcode: null,
        selectedAddress: null,
        manualAddress: null,
      },

    }
  },
  async mounted() {
    loadDepositPolicy();
    try {
      const optionsResponse = await api.get('/api/collection/options');
      const tempObj = optionsResponse.data.reduce((acc, opt) => {
        acc[opt.collectionLocation] = {
          collectionLocation: opt.collectionLocation,
          collectionFee: opt.collectionFee,
          collectionValue: opt.collectionValue,
          collectionMessage: opt.collectionMessage,
          enabled: false
        };
        return acc;
      }, {});

      this.collectionLocations = Object.values(tempObj);

      this.collectionOptions = optionsResponse.data;

      for (const o of this.collectionOptions) {
        if (o.enabled) {
          const i = this.collectionLocations.findIndex(x => x.collectionLocation === o.collectionLocation)
          this.collectionLocations[i].enabled = true;
        }
      }

      this.loadBasket();
      const slotsResponse = await api.get('/api/collection/slots');
      this.collectionSlots = slotsResponse.data;
      const deliveryDatesResponse = await api.get('/api/collection/delivery');
      this.deliveryDates = deliveryDatesResponse.data;
    } catch (e) {
      console.log(e.message)
    }
  },
  computed: {
    filteredDates() {
      return this.collectionOptions.filter(opt =>
          opt.collectionLocation === this.selectedLocation
      );
    },
    filteredTimes() {
      const selectedOption = this.collectionOptions.find(opt => opt.collectionDate === this.selectedDate && opt.collectionLocation === this.selectedLocation);
      if (selectedOption) {
        return this.collectionSlots.filter(slot => slot.collectionOption === selectedOption.collectionOptionId &&
            (slot.bookedCollections < slot.capacity));
      }
      return [];
    },
    // The sums below mirror POST /api/checkout/payment (worker/src/routes/checkout.ts): the deposit,
    // the donation, 1000p for delivery and the collection fee of the chosen location and date. All in
    // pence. The deposit follows GET /api/checkout/deposit-policy (WP-37, services/depositPolicy.js):
    // per item, a 2000p order deposit plus each basket line's product deposit; fixed, one deposit for
    // the whole order, capped at the estimated total (worker/src/lib/deposit.ts). Until the policy
    // is known no deposit figure is shown.
    depositMode() {
      if (depositPolicy.status !== 'ready') return null;
      // Percent of an estimate that is unknown or zero: the server takes the per-item deposit.
      if (depositPolicy.mode === 'percent' && percentDepositFor(this.estimatedTotal) === null) return 'perItem';
      return depositPolicy.mode;
    },
    estimatedTotal() {
      // Same formula as the basket's "Estimated Total" (BasketComponent.getBasket).
      return Math.round(this.basket.reduce((sum, row) => {
        const i = row.basketItem;
        const line = i.price * i.selectedWeight;
        // A quantity discount counts only once the line reaches its threshold, as each basket line shows.
        const discounted = i.discountAmount > 0 && i.selectedWeight >= i.discountQty;
        // A discounted line is rounded to the penny, as the server prices it (priceOrderLine, L-73),
        // so the estimate here is the one the deposit is worked out from (WP-37).
        return sum + (discounted ? Math.round((line * (100 - Math.min(i.discountAmount, 100))) / 100) : line);
      }, 0));
    },
    depositLines() {
      return this.basket
          .map(row => ({key: row.basketItemId, name: row.basketItem.item, amount: parseInt(row.basketItem.deposit) || 0}))
          .filter(line => line.amount > 0);
    },
    depositTotal() {
      if (this.depositMode === 'fixed') return fixedDepositFor(this.estimatedTotal);
      if (this.depositMode === 'percent') return percentDepositFor(this.estimatedTotal);
      return depositPolicy.baseDeposit + this.depositLines.reduce((sum, line) => sum + line.amount, 0);
    },
    depositCapped() {
      return this.depositMode === 'fixed' && this.depositTotal < depositPolicy.orderDeposit;
    },
    collectionFee() {
      if (this.selectedOption !== 'Collection' || !this.selectedLocation) return 0;
      // The server charges the fee of the row matching location AND date (getCollectionFee,
      // worker/src/routes/checkout.ts). Before a date is chosen, show a fee only when every date at
      // this location carries the same one; otherwise wait for the date.
      const forLocation = this.collectionOptions.filter(opt => opt.collectionLocation === this.selectedLocation);
      const exact = forLocation.find(opt => opt.collectionDate === this.selectedDate);
      if (exact) return parseInt(exact.collectionFee) || 0;
      const fees = [...new Set(forLocation.map(opt => parseInt(opt.collectionFee) || 0))];
      return fees.length === 1 ? fees[0] : 0;
    },
    collectionFeePending() {
      if (this.selectedOption !== 'Collection' || !this.selectedLocation) return false;
      const forLocation = this.collectionOptions.filter(opt => opt.collectionLocation === this.selectedLocation);
      if (forLocation.some(opt => opt.collectionDate === this.selectedDate)) return false;
      return new Set(forLocation.map(opt => parseInt(opt.collectionFee) || 0)).size > 1;
    },
    payToday() {
      return this.depositTotal + this.selectedDonation * 100 + (this.selectedOption === 'Delivery' ? 1000 : 0) + this.collectionFee;
    },
    balanceLater() {
      return Math.max(0, this.estimatedTotal - this.depositTotal);
    },
  },
  methods: {
    formatDepositAmount,
    retryDepositPolicy() {
      loadDepositPolicy();
    },
    async loadBasket() {
      const basketId = localStorage.getItem('basketId2026');
      if (!basketId) return;
      try {
        this.basket = (await api.get('/api/basket/checkout/' + basketId)).data;
      } catch (e) {
        this.basketFailed = true;
      }
    },
    async searchPostcode() {
      if (!this.postcode || this.isSearching) return;
      this.isSearching = true;
      this.lookupMessage = '';
      this.addresses = [];
      this.selectedAddress = null;
      try {
        const cleanPostcode = this.postcode.replace(/\s+/g, '');
        const addressResponse = await api.get(`https://location-service.lhbd.workers.dev/?postcode=${cleanPostcode}`);
        this.addresses = addressResponse.data?.data?.addresses || [];
        this.hasSearchedPostcode = true;
        if (this.addresses.length === 0) {
          this.lookupMessage = "We couldn't find any addresses for that postcode. Please type your address below.";
          this.showManualAddressInput = true;
        }
      } catch (e) {
        // The lookup service answers with a plain-text reason. Its own messages to the customer
        // (such as being outside the delivery area) are shown; technical ones are replaced.
        const reason = e.response && e.response.status < 500 && typeof e.response.data === 'string' ? e.response.data : '';
        if (reason && !/failed to fetch/i.test(reason)) {
          this.lookupMessage = reason;
        } else if (reason) {
          this.lookupMessage = "We couldn't find that postcode. Please check it, or type your address below.";
        } else {
          this.lookupMessage = "The address search isn't available right now. Please type your address below.";
        }
        this.showManualAddressInput = true;
      } finally {
        this.isSearching = false;
      }
    },
    getSelectedDateMessage() {
      if (this.selectedDate) {
        const selectedDateObj = this.filteredDates.find(
            date => date.collectionDate === this.selectedDate
        );
        return selectedDateObj && selectedDateObj.collectionMessage
            ? selectedDateObj.collectionMessage
            : null;
      }
      return null;
    },
    setDonation(value) {
      this.selectedDonation = value;
    },
    showManualInput() {
      this.selectedAddress = ""
      this.showManualAddressInput = true;
    },
    formatCurrency(amountInPence) {
      const pounds = amountInPence / 100;
      return new Intl.NumberFormat('en-GB', {style: 'currency', currency: 'GBP'}).format(pounds);
    },
    showSubmitError(message) {
      this.isCheckingOut = false;
      this.submitError = message;
      this.$nextTick(() => {
        const el = this.$refs.submitError;
        if (el) {
          el.scrollIntoView({block: 'center'});
          el.focus();
        }
      });
    },
    async submitForm() {
      if (!this.isCheckingOut) {
        this.submitError = '';
        if (this.validateForm()) {
          this.isCheckingOut = true;
          const payload = {
            staffId: this.staffId,
            firstname: this.firstname,
            lastname: this.lastname,
            email: this.email,
            phone: this.phone,
            selectedOption: this.selectedOption,
            donation: this.selectedDonation,
            basketId: localStorage.getItem('basketId2026'),
            mailingList: this.mailingList,
            comments: this.comments
          };

          if (this.selectedOption === 'Collection') {
            payload.collectionDetails = {
              selectedLocation: this.selectedLocation,
              selectedDate: this.selectedDate,
              selectedTime: this.selectedTime,
            };
          } else {
            payload.deliveryDetails = {
              selectedDeliveryDate: this.selectedDeliveryDate,
              postcode: this.postcode,
              address: this.selectedAddress || this.manualAddress,
            };
          }

          try {
            const paymentRes = await api.post('/api/checkout/payment', payload);
            const paymentApi = paymentRes.data;
            if (paymentApi.URL) {
              window.location.href = paymentApi.URL;
            } else {
              // The API can answer 200 with {error} (for example when items cannot be reserved).
              this.showSubmitError(paymentApi.error || "We couldn't start your payment. Please try again.");
            }
          } catch (e) {
            const serverError = e.response && e.response.data && e.response.data.error;
            this.showSubmitError(typeof serverError === 'string' && serverError
                ? serverError
                : "An error has occurred contacting our payment provider, please try again.");
          }
        } else {
          this.$nextTick(() => {
            const first = this.$refs.form.querySelector('.is-invalid');
            if (first) {
              first.scrollIntoView({block: 'center'});
              first.focus();
            }
          });
        }
      }
    },
    validateForm() {
      const errors = {};
      if (!this.firstname.trim()) errors.firstname = 'Please enter your first name';
      if (!this.lastname.trim()) errors.lastname = 'Please enter your last name';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) errors.email = 'Please enter a valid email address';
      if (!this.phone.trim()) errors.phone = 'Please enter a phone number';

      if (this.selectedOption === 'Collection') {
        if (!this.selectedLocation) errors.selectedLocation = 'Please choose where to collect';
        else if (!this.selectedDate) errors.selectedDate = 'Please choose a collection date';
        else if (!this.selectedTime) errors.selectedTime = 'Please choose a collection time';
      } else if (this.selectedOption === 'Delivery') {
        if (!this.selectedDeliveryDate) errors.selectedDeliveryDate = 'Please choose a delivery date';
        if (!this.postcode) errors.postcode = 'Please enter your postcode';
        if (!(this.selectedAddress || this.manualAddress.trim())) {
          errors.address = 'Please choose your address from the list or type it in';
          this.showManualAddressInput = this.showManualAddressInput || !this.addresses?.length;
        }
      }

      this.errors = errors;
      const validation = {};
      Object.keys(this.validation).forEach(field => {
        validation[field] = errors[field] ? 'is-invalid' : null;
      });
      if (errors.address) {
        validation[this.showManualAddressInput ? 'manualAddress' : 'selectedAddress'] = 'is-invalid';
      }
      this.validation = validation;
      return Object.keys(errors).length === 0;
    },
  }
}
</script>
<style scoped>
.field-error {
  color: var(--tblr-danger, #d63939);
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
</style>
